// Composant d'entrée monétaire moderne pour les formulaires U2000, avec design system unifié
import React, { useRef } from "react";
import U2000Label from "../label/U2000Label";
import U2000InvalidFeedback from "../feedback/U2000InvalidFeedback";
import type { U2000FormStateHandlers } from "../../../reducer/formReducer/U2000FormStateHandlers";
import { U2000FormStateEnumState } from "../../../reducer/formReducer/U2000FormState";
import {
  formatCurrency,
  parseCurrencyInput,
  getNewCursorPosition,
  isPartialDecimalInput,
  type CurrencyFormatOptions,
} from "../../../tools/U2000CurrencyUtils";
import { u2000SelectInputContentsOnFocus } from "../../../tools/U2000FormInputUtils";

interface U2000InputCurrencyProps {
  formStateHandlers: U2000FormStateHandlers<any>;
  fieldName: string;
  label: string;
  readonly?: boolean;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  icon?: React.ReactNode;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement>;
  // Options de formatage
  currencySymbol?: string;
  symbolPosition?: "left" | "right";
  decimalSeparator?: "." | ",";
  thousandSeparator?: " " | ",";
  allowNegative?: boolean;
  showNegativeInRed?: boolean;
  showNegativeInParentheses?: boolean;
  decimalDigits?: number;
  /** Plafond numérique (valeur absolue plafonnée à la saisie et au blur). */
  maxValue?: number;
}

// Classes CSS modernes avec design system unifié
const getInputClasses = (
  formStateHandlers: U2000FormStateHandlers<any>,
  fieldName: string,
  isFocused: boolean,
  readonly?: boolean,
  disabled?: boolean,
  showNegativeInRed?: boolean,
  isNegative?: boolean,
) => {
  // BL-0349 : un seul fond par état (pas bg-surface + bg-muted)
  const baseClasses = "w-full px-4 py-3 text-sm text-right border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2";
  const isFormRead = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const isDisabled = disabled || isFormRead;
  const isReadonly = readonly && !isDisabled;
  const hasError = formStateHandlers.getErrors()[fieldName] !== undefined;

  if (isDisabled) {
    return `${baseClasses} bg-muted text-gray-500 border-gray-200 cursor-not-allowed`;
  }

  if (isReadonly) {
    return `${baseClasses} bg-muted text-gray-700 border-gray-200 cursor-text`;
  }

  if (hasError) {
    return `${baseClasses} bg-surface border-red-300 focus:border-red-500 focus:ring-red-500/20 text-gray-900 placeholder:text-gray-400`;
  }

  if (isFocused) {
    const negativeClass = showNegativeInRed && isNegative ? "text-red-600" : "text-gray-900";
    return `${baseClasses} bg-surface border-blue-500 ring-blue-500/20 ${negativeClass} placeholder:text-gray-400`;
  }

  const negativeClass = showNegativeInRed && isNegative ? "text-red-600" : "text-gray-900";
  return `${baseClasses} bg-surface border-gray-300 focus:border-blue-500 focus:ring-blue-500/20 ${negativeClass} placeholder:text-gray-400 hover:border-gray-400`;
};

// Utilitaire pour accéder à une propriété imbriquée d'un objet à partir d'un chemin
function getNestedValue(formStateHandlers: U2000FormStateHandlers<any>, fieldName: string): any {
  const data = formStateHandlers.getData() as Record<string, any>;
  return fieldName.split(".").reduce((acc: any, part: string) => (acc !== undefined && acc !== null ? acc[part] : undefined), data);
}

function clampCurrencyToMax(value: number | null, maxValue: number | undefined): number | null {
  if (value === null || maxValue == null || !Number.isFinite(maxValue)) return value;
  const abs = Math.abs(value);
  if (abs > maxValue + 0.005) return maxValue;
  return value;
}

// Composant principal avec support de formatage monétaire
const U2000InputCurrency = ({
  formStateHandlers,
  fieldName,
  label,
  readonly = false,
  disabled = false,
  required = false,
  placeholder,
  icon,
  onFocus,
  onBlur,
  inputRef,
  currencySymbol = "$",
  symbolPosition = "left",
  decimalSeparator = ".",
  thousandSeparator = " ",
  allowNegative = true,
  showNegativeInRed = false,
  showNegativeInParentheses = false,
  decimalDigits = 2,
  maxValue,
}: U2000InputCurrencyProps) => {
  const [isFocused, setIsFocused] = React.useState(false);
  const [displayValue, setDisplayValue] = React.useState<string>("");
  const internalInputRef = useRef<HTMLInputElement>(null);
  const inputElementRef = inputRef || internalInputRef;
  const previousValueRef = useRef<string>("");
  const restoreZeroOnEmptyBlurRef = useRef(false);

  // Options de formatage
  const formatOptions: CurrencyFormatOptions = {
    currencySymbol,
    symbolPosition,
    decimalSeparator,
    thousandSeparator,
    allowNegative,
    showNegativeInParentheses,
    decimalDigits,
  };

  // Obtenir la valeur actuelle du formulaire (nombre brut)
  const currentNumericValue = getNestedValue(formStateHandlers, fieldName);
  
  // Initialiser displayValue si elle est vide et qu'on a une valeur dans le formulaire
  React.useEffect(() => {
    if (!isFocused && currentNumericValue !== null && currentNumericValue !== undefined && currentNumericValue !== "") {
      const formatted = formatCurrency(currentNumericValue, formatOptions);
      if (formatted !== displayValue) {
        setDisplayValue(formatted);
      }
    } else if (!isFocused && (currentNumericValue === null || currentNumericValue === undefined || currentNumericValue === "")) {
      if (displayValue !== "") {
        setDisplayValue("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNumericValue, isFocused]);

  // Déterminer si la valeur est négative pour l'affichage
  const isNegative = currentNumericValue !== null && currentNumericValue !== undefined && typeof currentNumericValue === "number" && currentNumericValue < 0;

  function buildSyntheticChangeEvent(value: string): React.ChangeEvent<HTMLInputElement> {
    return { target: { value, id: fieldName, name: fieldName } } as React.ChangeEvent<HTMLInputElement>;
  }

  function commitInputValue(rawInputValue: string) {
    let finalNumericValue: number | null = null;
    if (rawInputValue.trim() !== "") {
      finalNumericValue = parseCurrencyInput(rawInputValue, formatOptions);
    }
    finalNumericValue = clampCurrencyToMax(finalNumericValue, maxValue);

    if (finalNumericValue === null && restoreZeroOnEmptyBlurRef.current) {
      finalNumericValue = 0;
    }
    restoreZeroOnEmptyBlurRef.current = false;

    if (finalNumericValue !== null) {
      const finalFormatted = formatCurrency(finalNumericValue, formatOptions);
      previousValueRef.current = finalFormatted;
      setDisplayValue(finalFormatted);
      if (formStateHandlers.onChange) {
        formStateHandlers.onChange(buildSyntheticChangeEvent(finalFormatted), finalNumericValue);
      }
    } else {
      previousValueRef.current = "";
      setDisplayValue("");
      if (formStateHandlers.onChange) {
        formStateHandlers.onChange(buildSyntheticChangeEvent(""), null);
      }
    }
  }

  // Gestion du focus avec état moderne
  function handleFocus(e: React.FocusEvent<HTMLInputElement>) {
    u2000SelectInputContentsOnFocus(e);
    setIsFocused(true);
    if (currentNumericValue === 0) {
      restoreZeroOnEmptyBlurRef.current = true;
      setDisplayValue("");
      previousValueRef.current = "";
    } else {
      restoreZeroOnEmptyBlurRef.current = false;
    }
    if (onFocus) {
      onFocus(e);
    }
  }

  // Gestion du changement de valeur avec formatage en temps réel
  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = e.target;
    let inputValue = input.value;
    const cursorPosition = input.selectionStart || 0;

    // Normaliser le séparateur décimal si l'utilisateur a tapé une virgule ou un point
    // Permettre les deux formats mais normaliser vers le séparateur configuré
    const configuredSeparator = decimalSeparator || ".";
    if (configuredSeparator === "." && inputValue.includes(",") && !inputValue.includes(".")) {
      inputValue = inputValue.replace(",", ".");
    } else if (configuredSeparator === "," && inputValue.includes(".") && !inputValue.includes(",")) {
      inputValue = inputValue.replace(".", ",");
    }

    // Utiliser la valeur précédente sauvegardée ou la valeur affichée actuelle
    const oldFormattedValue = previousValueRef.current || displayValue;

    // Nettoyer la valeur d'entrée pour analyser la saisie brute (sans symboles de formatage)
    // Permettre à la fois le point et la virgule comme séparateur décimal
    const cleanedInput = inputValue.replace(/[^\d.,-]/g, "");
    const isPartialInput = isPartialDecimalInput(cleanedInput);
    
    // Détecter si l'utilisateur est en train de taper un séparateur décimal (point ou virgule) ou des décimales
    const hasDecimalSeparator = cleanedInput.includes(".") || cleanedInput.includes(",");
    
    // Normaliser temporairement vers le séparateur configuré pour la détection
    const normalizedInput = cleanedInput.replace(configuredSeparator === "," ? /\./g : /,/g, configuredSeparator);
    const decimalParts = normalizedInput.split(configuredSeparator);
    const decimalPartLength = decimalParts.length > 1 ? decimalParts[1].length : 0;
    const endsWithDecimalSeparator = cleanedInput.endsWith(".") || cleanedInput.endsWith(",");
    
    // L'utilisateur tape des décimales si :
    // - Il y a un séparateur décimal ET (le séparateur est à la fin OU le nombre de décimales est incomplet)
    const isTypingDecimals = hasDecimalSeparator && (endsWithDecimalSeparator || decimalPartLength < (decimalDigits || 2));
    
    // Si l'utilisateur est en train de taper des décimales, ne pas parser/formater
    // Cela évite les problèmes de double point (ex: "$15.4.00")
    let parsedValue: number | null = null;
    if (!isTypingDecimals) {
      parsedValue = parseCurrencyInput(inputValue, formatOptions);
    }

    // Si la valeur est invalide et ce n'est pas une saisie partielle, ne rien faire
    // Mais permettre la saisie de décimales même si le parsing échoue
    if (parsedValue === null && !isPartialInput && !isTypingDecimals && inputValue.trim() !== "" && inputValue !== "-" && inputValue !== "(") {
      // Valeur invalide, restaurer l'ancienne valeur
      setTimeout(() => {
        if (inputElementRef.current) {
          inputElementRef.current.value = oldFormattedValue;
          inputElementRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
      }, 0);
      return;
    }


    // Formater la nouvelle valeur
    let newFormattedValue: string;
    let valueToStore: number | null = null;
    
    // Stratégie : Ne formater QUE si la saisie est complète (pas de décimales en cours, pas de saisie partielle)
    // Sinon, garder la saisie telle quelle pour permettre à l'utilisateur de taper librement
    
    if (inputValue.trim() === "") {
      // Valeur vide, effacer
      newFormattedValue = "";
      valueToStore = null;
    } else if (inputValue === "-" || inputValue === "(") {
      // Permettre la saisie en cours (tiret ou parenthèse)
      newFormattedValue = inputValue;
      valueToStore = currentNumericValue; // Garder l'ancienne valeur pendant la saisie
    } else if (isTypingDecimals) {
      // L'utilisateur est en train de taper des décimales, permettre la saisie sans formater
      // Nettoyer la valeur pour éviter les doubles points (ex: "$15.4.00" → "$15.4")
      let cleanInputValue = inputValue;
      
      // Si la valeur contient le formatage ".00" ou ",00" à la fin et qu'on est en train de taper, le retirer
      // Ex: "$15.4.00" → "$15.4" ou "$15.45.00" → "$15.45" ou "15,4,00" → "15,4"
      const formattedEnding = configuredSeparator === "," ? ",00" : ".00";
      if (hasDecimalSeparator && inputValue.includes(formattedEnding)) {
        // Vérifier si c'est un double séparateur (ex: "$15.4.00" ou "15,4,00")
        const doubleSeparatorPattern = configuredSeparator === "," 
          ? new RegExp(`(\\d+)[,.]?(\\d+),00$`)
          : new RegExp(`(\\d+)[,.]?(\\d+)\\.00$`);
        const doubleSeparatorMatch = inputValue.match(doubleSeparatorPattern);
        if (doubleSeparatorMatch) {
          // C'est un double séparateur, garder seulement la partie avant le deuxième séparateur
          cleanInputValue = inputValue.replace(new RegExp(`${formattedEnding.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), "");
        } else if (endsWithDecimalSeparator || decimalPartLength < (decimalDigits || 2)) {
          // Retirer le ".00" ou ",00" formaté si on est en train de taper
          cleanInputValue = inputValue.replace(new RegExp(`${formattedEnding.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), "");
        }
      }
      
      newFormattedValue = cleanInputValue;
      // Parser la valeur nettoyée pour obtenir la valeur numérique correcte
      const cleanedParsedValue = parseCurrencyInput(cleanInputValue, formatOptions);
      valueToStore = cleanedParsedValue !== null ? cleanedParsedValue : currentNumericValue;
    } else if (isPartialInput) {
      // Saisie partielle (sans décimales), permettre la saisie sans formater immédiatement
      newFormattedValue = inputValue;
      // Parser pour obtenir la valeur numérique
      const cleanedParsedValue = parseCurrencyInput(inputValue, formatOptions);
      valueToStore = cleanedParsedValue !== null ? cleanedParsedValue : currentNumericValue;
    } else if (parsedValue !== null) {
      // Valeur complète et valide, formater
      newFormattedValue = formatCurrency(parsedValue, formatOptions);
      valueToStore = parsedValue;
    } else {
      // Valeur invalide, garder l'ancienne
      newFormattedValue = oldFormattedValue;
      valueToStore = currentNumericValue;
    }

    valueToStore = clampCurrencyToMax(valueToStore, maxValue);
    if (
      valueToStore !== null &&
      maxValue != null &&
      Number.isFinite(maxValue) &&
      parsedValue !== null &&
      parsedValue > maxValue + 0.005 &&
      !isTypingDecimals
    ) {
      newFormattedValue = formatCurrency(valueToStore, formatOptions);
    }

    // Calculer la nouvelle position du curseur seulement si on a une valeur formatée valide
    let newCursorPosition = cursorPosition;
    if (newFormattedValue !== inputValue && newFormattedValue !== "-" && newFormattedValue !== "(") {
      newCursorPosition = getNewCursorPosition(
        oldFormattedValue,
        newFormattedValue,
        cursorPosition,
        formatOptions
      );
    }

    // Sauvegarder la nouvelle valeur formatée pour le prochain changement
    previousValueRef.current = newFormattedValue;
    
    // Mettre à jour la valeur affichée
    setDisplayValue(newFormattedValue);

    // Mettre à jour la valeur numérique dans le formulaire
    if (formStateHandlers.onChange) {
      formStateHandlers.onChange(e, valueToStore);
    }

    // Restaurer la position du curseur après le rendu
    setTimeout(() => {
      if (inputElementRef.current) {
        inputElementRef.current.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    }, 0);
  }

  // Gestion de la saisie au clavier pour valider les caractères
  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    // Permettre les touches de navigation et de suppression
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Home",
      "End",
      "Tab",
      "Enter",
    ];

    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      if (e.key === "Tab" || e.key === "Enter") {
        commitInputValue(inputElementRef.current?.value ?? "");
      }
      return; // Autoriser ces touches
    }

    // Bloquer le signe négatif si allowNegative est false
    if (e.key === "-" && !allowNegative) {
      e.preventDefault();
      return;
    }

    // Autoriser les chiffres, le point, la virgule (tous deux comme séparateurs décimaux), le tiret et les parenthèses
    if (e.key.length === 1) {
      const isValidChar = /[0-9.,\-()]/.test(e.key);
      if (!isValidChar) {
        e.preventDefault();
      }
      
      // Si l'utilisateur tape une virgule ou un point, vérifier s'il y a déjà un séparateur
      // La normalisation se fera dans handleChange pour éviter les problèmes de curseur
      if (e.key === "," || e.key === ".") {
        const currentValue = inputElementRef.current?.value || "";
        const hasDecimal = currentValue.includes(".") || currentValue.includes(",");
        
        // Si un séparateur décimal existe déjà, empêcher l'ajout d'un autre
        if (hasDecimal) {
          e.preventDefault();
          return;
        }
        // Sinon, laisser le navigateur insérer le caractère normalement
        // La normalisation vers le séparateur configuré se fera dans handleChange
      }
    }
  }

  // Gestion du blur avec validation finale
  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const inputValue = e.target.value;
    if (formStateHandlers.onBlur) {
      formStateHandlers.onBlur(e);
    }

    setTimeout(() => {
      setIsFocused(false);
      const latestValue = inputElementRef.current?.value ?? inputValue;
      commitInputValue(latestValue);
      if (onBlur) {
        onBlur(e);
      }
    }, 0);
  }

  const isFormRead = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const inputDisabled = disabled || isFormRead;
  const inputReadOnly = readonly && !inputDisabled;

  return (
    <div className="space-y-1.5">
      <U2000Label label={label} required={required} htmlFor={fieldName} />

      <div className="relative">
        {icon && <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none z-10">{icon}</div>}

        <input
          ref={inputElementRef}
          type="text"
          id={fieldName}
          name={fieldName}
          placeholder={placeholder}
          className={`${getInputClasses(formStateHandlers, fieldName, isFocused, readonly, disabled, showNegativeInRed, isNegative)} ${icon ? "pl-10" : ""}`}
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={inputDisabled}
          readOnly={inputReadOnly}
          required={required}
          aria-invalid={!!(formStateHandlers.getTouched()[fieldName] && formStateHandlers.getErrors()[fieldName])}
          aria-describedby={formStateHandlers.getErrors()[fieldName] ? `${fieldName}-error` : undefined}
        />
      </div>

      <U2000InvalidFeedback formStateHandlers={formStateHandlers} fieldName={fieldName} />
    </div>
  );
};

export default U2000InputCurrency;
